// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDButton from "theme/components/MDButton";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDBadge from "theme/components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import { useMaterialUIController } from "context";

import { useAuthUser, useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import {
  Snackbar,
  Alert,
  TextField,
  Grid,
  Icon,
  Card,
  AlertTitle,
  CircularProgress,
  Backdrop,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import * as Yup from "yup";
import { Formik } from "formik";
import usePageTitle from "app/pageTitle";
import CompanyNotification from "app/company_notification";

// import { styled } from "@mui/material/styles";

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be atleast 6 character length")
    .required("Password is required!"),
  c_password: Yup.string().oneOf([Yup.ref("password"), null], "Passwords must match"),
});

const initialValues = {
  c_password: "",
  password: "",
};

// const ImgStyled = styled("img")(({ theme }) => ({
//   width: 120,
//   height: 120,
//   marginRight: theme.spacing(6.25),
//   borderRadius: theme.shape.borderRadius,
// }));

function UserProfile() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const title = "My Profile";
  usePageTitle(title);

  const auth = useAuthUser();
  const user = auth();
  const [accountInfo, setAccountInfo] = useState(user);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const [vLink, setVLink] = useState(null);

  const [showCompanyNotification, setShowCompanyNotification] = useState(false);
  const [companyNotification, setCompanyNotification] = useState(null);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("users/profile.json")
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setAccountInfo(result.data.data.profile);
            setVLink(result.data.data.verification_link);

            if (result.data.notification) {
              setCompanyNotification(result.data.notification);
              if (result.data.notification.pages.includes("ALL")) {
                setShowCompanyNotification(true);
              } else if (result.data.notification.pages.includes("profile")) {
                setShowCompanyNotification(true);
              }
            }
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function sendVerificationLink() {
    if (vLink) {
      window.open(vLink);
    } else {
      setShowLoading(true);
      await axios
        .get("users/send-kyc-link.json")
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setSeverity("success");
            // setErrMsg("Verification link sent!");
            setErrMsg(result.data.data.message);
            setOpen(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred.");
          setOpen(true);
        });
    }
  }

  // async function getKYCAddressVerificationLink() {
  //   if (!accountInfo.is_verified) {
  //     setSeverity("error");
  //     setErrMsg("Identify verification is required before proceeding.");
  //     setOpen(true);
  //     return;
  //   }

  //   setShowLoading(true);
  //   await axios
  //     .get("users/get-kyc-address-link.json")
  //     .then((result) => {
  //       setShowLoading(false);
  //       if (result.data.status === "success") {
  //         setSeverity("success");
  //         setErrMsg("Redirecting to verification page...");
  //         setOpen(true);
  //         window.location.href = result.data.data.result;
  //       } else {
  //         setSeverity("error");
  //         setErrMsg(result.data.data.message);
  //         setOpen(true);
  //       }
  //     })
  //     .catch(() => {
  //       setShowLoading(false);
  //       setSeverity("error");
  //       setErrMsg("An error occurred. Make sure you have an active internet connection.");
  //       setOpen(true);
  //     });
  // }

  const postData = async (params) => {
    setShowLoading(true);
    // const json = JSON.stringify(params);
    await axios
      .post("users/update.json", params)
      .then((response) => {
        setShowLoading(false);
        if (response.data.status === "success") {
          setSeverity("success");
          setErrMsg("Password changed successfully");
          setOpen(true);
        } else {
          setSeverity("error");
          setErrMsg(response.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  };

  const handleFormSubmit = (values) => {
    if (values.password !== values.c_password) {
      setSeverity("error");
      setErrMsg("Passwords do not match");
      setOpen(true);
      return;
    }

    const params = {
      password: values.password,
    };

    setOpenPasswordDialog(false);
    postData(params);
  };

  const [openTerminateDialog, setTerminateDialog] = useState(false);
  const signOut = useSignOut();
  const navigate = useNavigate();
  const authSignOut = () => {
    signOut();
    navigate("/user/sign-in");
  };

  async function terminateAccount() {
    setShowLoading(true);
    await axios
      .post("users/terminate.json")
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          authSignOut();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  const [currentBvn, setBVN] = useState(null);
  const [openBVNDialog, setOpenBVNDialog] = useState(false);

  const [currentPin, setPin] = useState(null);
  const [openPinDialog, setOpenPinDialog] = useState(false);

  const [currentHashtag, setHashTag] = useState(null);
  const [openHashTagDialog, setOpenHashTagDialog] = useState(false);

  async function submitBVN() {
    if (currentBvn === null) {
      setSeverity("error");
      setErrMsg("Enter a valid 11 digit BVN.");
      setOpen(true);
      return;
    }
    setOpenBVNDialog(false);
    setShowLoading(true);
    const json = { bvn: `${currentBvn}` };
    await axios
      .post("users/verify-bvn.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  async function submitPin() {
    if (currentPin === null || currentPin.length !== 4) {
      setSeverity("error");
      setErrMsg("Enter a valid 4 digit Pin.");
      setOpen(true);
      return;
    }
    setOpenPinDialog(false);
    setShowLoading(true);
    const json = { pin: `${currentPin}` };
    await axios
      .post("users/update.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Pin set successfully.");
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  async function resetPin() {
    setShowLoading(true);
    await axios
      .post("users/forgot-pin.json")
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Pin reset request sent successfully. Please check your registered email.");
          setOpen(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  async function submitHashTag() {
    if (currentHashtag === null || currentHashtag.length < 3) {
      setSeverity("error");
      setErrMsg("Enter a hashtag at least 3 characters long.");
      setOpen(true);
      return;
    }

    setOpenHashTagDialog(false);
    setShowLoading(true);
    const json = { username: `${currentHashtag}` };
    await axios
      .post("users/update.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("HashTag set successfully.");
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: accountInfo.is_verified ? "top" : "bottom",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {companyNotification !== null && showCompanyNotification ? (
        <CompanyNotification
          title={companyNotification.title}
          description={companyNotification.description}
        />
      ) : (
        <> </>
      )}
      <Dialog open={openTerminateDialog}>
        <DialogTitle>
          <MDBox>Delete Account?</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              Are you sure you want to delete your account? All funds will be lost after account
              deletion.
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            mr={3}
            onClick={() => {
              setTerminateDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            No
          </MDButton>
          <MDButton
            color="error"
            variant="outlined"
            onClick={() => {
              setTerminateDialog(false);
              terminateAccount();
            }}
          >
            Yes
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openPinDialog}>
        <DialogTitle>
          <MDBox>Set Pin</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Secure your account with a Pin</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDInput
              inputProps={{ maxLength: 4 }}
              name="pin"
              margin="dense"
              label="Pin"
              type="password"
              fullWidth
              variant="outlined"
              onChange={(e) => setPin(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            mr={3}
            onClick={() => {
              setOpenPinDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              submitPin();
            }}
          >
            Set Pin
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openHashTagDialog}>
        <DialogTitle>
          <MDBox>Create Your Hashtag</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Send and receive funds using #hashtags. Create your unique hashtag.</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDInput
              inputProps={{ maxLength: 50 }}
              name="hashtag"
              margin="dense"
              label="HashTag"
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setHashTag(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            mr={3}
            onClick={() => {
              setOpenHashTagDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              submitHashTag();
            }}
          >
            Create HashTag
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openBVNDialog}>
        <DialogTitle>
          <MDBox>Verify BVN</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Enter your valid BVN</MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDInput
              name="bvn"
              margin="dense"
              label="BVN"
              type="number"
              fullWidth
              variant="outlined"
              onChange={(e) => setBVN(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            mr={3}
            onClick={() => {
              setOpenBVNDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="error"
            variant="outlined"
            onClick={() => {
              submitBVN();
            }}
          >
            Verify
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openPasswordDialog}>
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <DialogTitle>
                <MDBox>Change Password</MDBox>
              </DialogTitle>
              <DialogContent>
                <MDBox>
                  <TextField
                    name="password"
                    margin="dense"
                    label="New Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={values.password || ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.password && errors.password}
                    error={Boolean(errors.password && touched.password)}
                  />
                  <TextField
                    margin="dense"
                    name="c_password"
                    label="Repeat Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={values.c_password || ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.c_password && errors.c_password}
                    error={Boolean(errors.c_password && touched.c_password)}
                  />
                </MDBox>
              </DialogContent>
              <DialogActions>
                <MDButton
                  mr={3}
                  onClick={() => {
                    setOpenPasswordDialog(false);
                  }}
                  color="error"
                  variant="outlined"
                >
                  Cancel
                </MDButton>
                <MDButton loading={showLoading} color="dark" variant="outlined" type="submit">
                  Change Password
                </MDButton>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
      <MDBox pt={1.5} pb={3} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {accountInfo === null ? (
              <> </>
            ) : (
              <>
                {accountInfo.is_verified === true ? (
                  <> </>
                ) : (
                  <MDBox mb={3}>
                    <Alert severity="error" sx={{ width: "100%" }}>
                      <AlertTitle>ID Verification Required</AlertTitle>
                      Kindly click the link below to complete your ID verification process.
                      <br />
                      <br />
                      <MDButton
                        loading={showLoading}
                        variant="contained"
                        color="error"
                        onClick={() => sendVerificationLink()}
                      >
                        <Icon>send</Icon>
                        <span sx={{ ml: 2 }}>&nbsp;Verify My Account</span>
                      </MDButton>
                    </Alert>
                  </MDBox>
                )}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6} lg={6}>
                    <Card sx={{ width: "100%", marginTop: "10px" }}>
                      <MDBox mt={1}>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                            Account Information
                          </MDTypography>
                        </MDBox>
                        <TableContainer component={Paper}>
                          <Table aria-label="personal information">
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Alero ID
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.account_identifier}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Last Name
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.last_name}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Other Names
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.first_name}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Gender
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.gender}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Date Of Birth
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.dob}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Email Address
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.email_address}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Phone Number
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    {accountInfo.phone_number}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    </Card>
                    <MDBox mt={3}>
                      <Card sx={{ width: "100%", marginTop: "25px" }}>
                        <MDBox mt={1}>
                          <TableContainer component={Paper}>
                            <Table aria-label="secure pin">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      #HashTag
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="h6" fontWeight="light">
                                      {accountInfo.username ? (
                                        <span>{accountInfo.username}</span>
                                      ) : (
                                        <MDButton
                                          // badgeContent={accountInfo.pin ? "Reset Pin" : "Set Pin"}
                                          size="small"
                                          variant="outlined"
                                          container
                                          color="dark"
                                          onClick={() => {
                                            setOpenHashTagDialog(true);
                                          }}
                                        >
                                          Set HashTag
                                        </MDButton>
                                      )}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      </Card>
                    </MDBox>
                    <MDBox mt={3}>
                      <Card sx={{ width: "100%", marginTop: "25px" }}>
                        <MDBox mt={1}>
                          <TableContainer component={Paper}>
                            <Table aria-label="secure pin">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      Secure Pin
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="h6" fontWeight="light">
                                      <MDButton
                                        // badgeContent={accountInfo.pin ? "Reset Pin" : "Set Pin"}
                                        size="small"
                                        variant="outlined"
                                        container
                                        color="dark"
                                        onClick={() => {
                                          if (!accountInfo.pin) {
                                            setOpenPinDialog(true);
                                          } else {
                                            resetPin();
                                          }
                                        }}
                                      >
                                        {accountInfo.pin ? "Reset Pin" : "Set Pin"}
                                      </MDButton>
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      </Card>
                    </MDBox>
                    <MDBox mt={3}>
                      <Card sx={{ width: "100%", marginTop: "25px" }}>
                        <MDBox mt={1}>
                          <TableContainer component={Paper}>
                            <Table aria-label="secure pin">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      Password
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="h6" fontWeight="light">
                                      <MDButton
                                        // badgeContent={accountInfo.pin ? "Reset Pin" : "Set Pin"}
                                        size="small"
                                        variant="outlined"
                                        container
                                        color="dark"
                                        onClick={() => setOpenPasswordDialog(true)}
                                      >
                                        Change Password
                                      </MDButton>
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      </Card>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <Card sx={{ width: "100%", marginTop: "10px" }}>
                      <MDBox mt={1}>
                        <MDBox p={3}>
                          <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                            KYC ID Information
                          </MDTypography>
                        </MDBox>
                        <TableContainer component={Paper}>
                          <Table aria-label="kyc information">
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    Status
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="right">
                                  <MDTypography variant="h6" fontWeight="light">
                                    <MDBadge
                                      badgeContent={
                                        accountInfo.is_verified ? "Verified" : "Not Verified"
                                      }
                                      variant="contained"
                                      container
                                      color={accountInfo.is_verified ? "success" : "error"}
                                    />
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              {accountInfo.is_verified ? (
                                <>
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        Photo
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="h6" fontWeight="light">
                                        <img
                                          alt="selfie"
                                          src={accountInfo.photo}
                                          style={{
                                            width: "30%",
                                            borderRadius: "5%",
                                            border: "1.5px",
                                            borderColor: "limegreen",
                                          }}
                                        />
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        ID Type
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="h6" fontWeight="light">
                                        {accountInfo.document_type}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        Document Number
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="h6" fontWeight="light">
                                        {accountInfo.document_number}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        Document Country
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="h6" fontWeight="light">
                                        {accountInfo.document_country}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography variant="h6" fontWeight="medium">
                                        Document Expiry Date
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="h6" fontWeight="light">
                                        {accountInfo.document_exp_date}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                </>
                              ) : (
                                <> </>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    </Card>
                    {/* <MDBox mt={3}>
                      <Card sx={{ width: "100%", marginTop: "25px" }}>
                        <MDBox mt={1}>
                          <MDBox p={3}>
                            <MDTypography
                              variant="h6"
                              fontWeight="medium"
                              textTransform="capitalize"
                            >
                              KYC Address Information
                            </MDTypography>
                          </MDBox>
                          <TableContainer component={Paper}>
                            <Table aria-label="kyc information">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      Status
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="h6" fontWeight="light">
                                      <MDBadge
                                        badgeContent={
                                          accountInfo.is_address_verified
                                            ? "Verified"
                                            : "Not Verified"
                                        }
                                        variant="contained"
                                        container
                                        color={
                                          accountInfo.is_address_verified ? "success" : "error"
                                        }
                                      />
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      Registered Address
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {accountInfo.is_address_verified ? (
                                      <MDTypography variant="h6" fontWeight="light">
                                        {accountInfo.address}
                                      </MDTypography>
                                    ) : (
                                      <MDButton
                                        size="small"
                                        variant="outlined"
                                        color={darkMode ? "white" : "dark"}
                                        onClick={() => {
                                          getKYCAddressVerificationLink();
                                        }}
                                      >
                                        Click to verify
                                      </MDButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      </Card>
                    </MDBox> */}
                    <MDBox mt={3}>
                      <Card sx={{ width: "100%", marginTop: "25px" }}>
                        <MDBox mt={1}>
                          <MDBox p={3}>
                            <MDTypography
                              variant="h6"
                              fontWeight="medium"
                              textTransform="capitalize"
                            >
                              BVN Verification
                            </MDTypography>
                          </MDBox>
                          <TableContainer component={Paper}>
                            <Table aria-label="kyc information">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      Status
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="h6" fontWeight="light">
                                      <MDBadge
                                        badgeContent={
                                          accountInfo.is_bvn_verified ? "Verified" : "Not Verified"
                                        }
                                        variant="contained"
                                        container
                                        color={accountInfo.is_bvn_verified ? "success" : "error"}
                                      />
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography variant="h6" fontWeight="medium">
                                      BVN
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {accountInfo.is_bvn_verified ? (
                                      <MDTypography variant="h6" fontWeight="light">
                                        {accountInfo.bvn}
                                      </MDTypography>
                                    ) : (
                                      <MDButton
                                        size="small"
                                        variant="outlined"
                                        color={darkMode ? "white" : "dark"}
                                        onClick={() => {
                                          if (accountInfo.is_verified === false) {
                                            setSeverity("error");
                                            setErrMsg(
                                              "Please complete your ID verification before proceeding"
                                            );
                                            setOpen(true);
                                            return;
                                          }
                                          setOpenBVNDialog(true);
                                        }}
                                      >
                                        Click to verify
                                      </MDButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </MDBox>
                      </Card>
                    </MDBox>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        {/* <MDBox mt={5}>
          <Card sx={{ height: "100%", marginTop: "0px" }}>
            <MDBox p={3} sx={{ textAlign: "right" }}>
              <MDButton
                variant="contained"
                color="error"
                onClick={() => {
                  setTerminateDialog(true);
                }}
              >
                Delete my Account
              </MDButton>
            </MDBox>
          </Card>
        </MDBox> */}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserProfile;
